import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../ui/LanguageSelector';
import { whitelabelService, WhitelabelBranding } from '../../services/whitelabelService';

// Helper function to darken/lighten a hex color
const adjustColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
  return `#${((1 << 24) + (R << 16) + (G << 8) + B).toString(16).slice(1)}`;
};

const AuthLayout: React.FC = () => {
  const { t } = useTranslation();
  const [whitelabel, setWhitelabel] = useState<WhitelabelBranding | null>(null);

  // Check for whitelabel branding based on current domain
  useEffect(() => {
    const checkWhitelabel = async () => {
      try {
        const host = window.location.host;
        const response = await whitelabelService.checkDomain(host);
        if (response.success && response.data) {
          setWhitelabel(response.data);
          // Apply primary color to CSS custom property
          if (response.data.primary_color) {
            document.documentElement.style.setProperty('--whitelabel-primary', response.data.primary_color);
          }
          // Update favicon if provided
          if (response.data.favicon_url) {
            const link = document.querySelector("link[rel*='icon']") as HTMLLinkElement || document.createElement('link');
            link.type = 'image/x-icon';
            link.rel = 'shortcut icon';
            link.href = response.data.favicon_url;
            document.getElementsByTagName('head')[0].appendChild(link);
          }
          // Update document title if company name provided
          if (response.data.company_name) {
            document.title = response.data.company_name;
          }
        }
      } catch (error) {
        // Silently fail for whitelabel check
      }
    };
    checkWhitelabel();
  }, []);

  const logoUrl = whitelabel?.logo_url || '/devskin-logo.png';
  const companyName = whitelabel?.company_name || 'DevSkin Marketing';
  const primaryColor = whitelabel?.primary_color || '#6366f1';

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:max-w-md">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <img src={logoUrl} alt={companyName} className="w-10 h-10 rounded-lg object-contain" />
            <span className="text-xl font-bold text-gray-900 dark:text-white">{companyName}</span>
          </div>

          {/* Form Content */}
          <Outlet />
        </div>

        {/* Language Selector */}
        <div className="mt-8 mx-auto">
          <LanguageSelector />
        </div>
      </div>

      {/* Right Side - Image/Branding */}
      <div className="hidden lg:flex lg:flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: whitelabel?.primary_color
              ? `linear-gradient(135deg, ${primaryColor} 0%, ${adjustColor(primaryColor, -30)} 100%)`
              : undefined
          }}
        ></div>
        {!whitelabel && <div className="absolute inset-0 gradient-primary"></div>}
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12">
          <div className="max-w-md text-center">
            <h2 className="text-3xl font-bold mb-4">
              {t('auth.startTrial')}
            </h2>
            <p className="text-lg text-white/80 mb-8">
              {t('dashboard.title')} - {t('campaigns.title')}, {t('keywords.title')}, {t('content.title')}
            </p>

            <div className="space-y-4 text-left">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{t('campaigns.google')} & {t('campaigns.meta')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{t('competitors.title')} & {t('keywords.title')}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span>{t('content.generateContent')} AI</span>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-black/20 to-transparent"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl"></div>
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl"></div>
      </div>
    </div>
  );
};

export default AuthLayout;
